/* eslint-disable replo/no-tailwind-literal-color */
import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type {
  LogLevel,
  ProcessLogPayload,
  ProcessType,
} from "schemas/generated/agentConnection";

import * as React from "react";

import { MAX_LOGS } from "@agent-ui/store/debugSandboxProcessLogsStore";
import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { Button } from "@replo/design-system/components/button/Button";
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxHeader,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@replo/design-system/components/field/Field";
import { Input } from "@replo/design-system/components/input/Input";
import twMerge from "@replo/design-system/utils/twMerge";
import { ScrollToBottomButton } from "@v2/features/agent-debugger/tabs/ScrollToBottomButton";
import { Clipboard, Trash2 } from "lucide-react";
import { StickToBottom } from "use-stick-to-bottom";

const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  debug: "text-slate-400",
  info: "text-blue-600",
  warn: "text-yellow-600",
  error: "text-red-600",
  fatal: "text-red-800 font-bold",
};

function formatLogTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString();
}

function formatLogForCopy(log: ProcessLogPayload): string {
  const time = formatLogTimestamp(log.timestamp);
  return `${time} [${log.timestamp}] [${log.level.toUpperCase()}] [${log.processType}] [${log.sandboxId}] ${log.message}`;
}

const LogEntry: React.FC<{ log: ProcessLogPayload }> = ({ log }) => {
  return (
    <div className="flex items-start gap-2 rounded px-1 py-0.5 text-xs hover:bg-slate-50">
      <span className="w-20 shrink-0 text-xs text-slate-400">
        {formatLogTimestamp(log.timestamp)}
      </span>
      <span className="w-18 shrink-0 rounded bg-slate-100 px-1 text-center text-xs text-slate-600">
        {log.processType}
      </span>
      <span
        className={twMerge(
          "w-10 shrink-0 text-xs uppercase",
          LOG_LEVEL_COLORS[log.level],
        )}
      >
        {log.level}
      </span>
      <span
        className="w-20 shrink-0 truncate rounded bg-slate-100 px-1 text-center text-xs text-slate-500"
        title={log.sandboxId}
      >
        {log.sandboxId}
      </span>
      <span className="flex-1 whitespace-pre-wrap break-all text-xs text-slate-700">
        {log.message}
      </span>
    </div>
  );
};

const LogsContent: React.FC<{
  filteredLogs: ProcessLogPayload[];
}> = ({ filteredLogs }) => {
  return (
    <StickToBottom.Content className="flex flex-col">
      {filteredLogs.length === 0 ? (
        <span className="text-xs text-slate-400">No logs received yet</span>
      ) : (
        <div className="space-y-0.5">
          {filteredLogs.map((log, i) => (
            <LogEntry key={`${log.timestamp}-${i}`} log={log} />
          ))}
        </div>
      )}
    </StickToBottom.Content>
  );
};

const CopyLogsButton: React.FC<{ logs: ProcessLogPayload[] }> = ({ logs }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    const formattedLogs = logs.map(formatLogForCopy).join("\n");
    await navigator.clipboard.writeText(formattedLogs);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (logs.length === 0) {
    return null;
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      startEnhancer={<Clipboard size={12} />}
      UNSAFE_className="absolute bottom-2 left-2 shadow"
      onClick={() => void handleCopy()}
    >
      {copied ? "Copied!" : "Copy Logs"}
    </Button>
  );
};

const ClearLogsButton: React.FC<{
  logsCount: number;
  onClearLogs: () => void;
}> = ({ logsCount, onClearLogs }) => {
  if (logsCount === 0) {
    return null;
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      startEnhancer={<Trash2 size={12} />}
      UNSAFE_className="absolute bottom-2 left-28 shadow"
      onClick={onClearLogs}
    >
      Clear Logs
    </Button>
  );
};

const LogFilterCombobox: React.FC<{
  options: { value: string; label: string }[];
  value: string[];
  onChange: (values: string[]) => void;
  allLabel: string;
}> = ({ options, value, onChange, allLabel }) => {
  const items: ComboboxItemWithValue[] = options.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const selectedItems = items.filter((item) => value.includes(item.value));

  const displayTitle =
    value.length === 0
      ? allLabel
      : value
          .map((v) => options.find((opt) => opt.value === v)?.label ?? v)
          .join(", ");

  return (
    <ComboboxRoot
      multiple
      items={items}
      value={selectedItems}
      onValueChange={(newItems) => {
        onChange(newItems.map((item) => item.value));
      }}
      itemToStringValue={(item) => String(item.label)}
      isItemEqualToValue={(a, b) => a.value === b.value}
    >
      <ComboboxTriggerButton size="sm">{displayTitle}</ComboboxTriggerButton>
      <ComboboxContent UNSAFE_className="w-40">
        <ComboboxHeader>
          <ComboboxInput placeholder="Search..." />
        </ComboboxHeader>
        <ComboboxEmpty />
        <ComboboxList>
          {(item: ComboboxItemWithValue) => (
            <ComboboxItem key={item.value} {...item} value={item} />
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
};

const LogsTab: React.FC<{
  processLogs: ProcessLogPayload[];
  onClearLogs: () => void;
}> = ({ processLogs, onClearLogs }) => {
  const [selectedProcessTypes, setSelectedProcessTypes] = React.useState<
    string[]
  >([]);
  const [selectedLogLevels, setSelectedLogLevels] = React.useState<string[]>(
    [],
  );
  const [selectedSandboxIds, setSelectedSandboxIds] = React.useState<string[]>(
    [],
  );
  const [searchQuery, setSearchQuery] = React.useState("");

  const processTypeOptions = React.useMemo(() => {
    const uniqueTypes = new Set(processLogs.map((log) => log.processType));
    return [...uniqueTypes].map((pt) => ({
      value: pt,
      label: pt,
    }));
  }, [processLogs]);

  const logLevelOptions = React.useMemo(() => {
    const uniqueLevels = new Set(processLogs.map((log) => log.level));
    return [...uniqueLevels].map((level) => ({
      value: level,
      label: level.charAt(0).toUpperCase() + level.slice(1),
    }));
  }, [processLogs]);

  const sandboxIdOptions = React.useMemo(() => {
    const uniqueIds = new Set(processLogs.map((log) => log.sandboxId));
    return [...uniqueIds].map((id) => ({
      value: id,
      label: id.length > 12 ? `${id.slice(0, 12)}…` : id,
    }));
  }, [processLogs]);

  const filteredLogs = React.useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase();
    return processLogs.filter((log) => {
      if (
        selectedProcessTypes.length > 0 &&
        !selectedProcessTypes.includes(log.processType)
      ) {
        return false;
      }
      if (
        selectedLogLevels.length > 0 &&
        !selectedLogLevels.includes(log.level)
      ) {
        return false;
      }
      if (
        selectedSandboxIds.length > 0 &&
        !selectedSandboxIds.includes(log.sandboxId)
      ) {
        return false;
      }
      if (lowerQuery && !log.message.toLowerCase().includes(lowerQuery)) {
        return false;
      }
      return true;
    });
  }, [
    processLogs,
    selectedProcessTypes,
    selectedLogLevels,
    selectedSandboxIds,
    searchQuery,
  ]);

  const oldestLogTimestamp = processLogs[0]?.timestamp;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      {processLogs.length === MAX_LOGS && oldestLogTimestamp && (
        <Alert
          variant="warning"
          description={`Log buffer full (${MAX_LOGS} logs). Oldest log from ${formatLogTimestamp(oldestLogTimestamp)}.`}
        />
      )}
      <div className="shrink-0 space-y-2 border-b border-slate-200 pb-3">
        <Field size="xs">
          <FieldLabel size="xs">Search</FieldLabel>
          <Input
            size="sm"
            placeholder="Filter by message content…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <FieldDescription size="xs">
              {filteredLogs.length} matching log
              {filteredLogs.length !== 1 ? "s" : ""}
            </FieldDescription>
          )}
        </Field>
        <div className="flex items-start gap-2">
          <Field size="xs" className="flex-1">
            <FieldLabel size="xs">Process</FieldLabel>
            <LogFilterCombobox
              options={processTypeOptions}
              value={selectedProcessTypes}
              onChange={(values) => {
                setSelectedProcessTypes(values as ProcessType[]);
              }}
              allLabel="All"
            />
          </Field>
          <Field size="xs" className="flex-1">
            <FieldLabel size="xs">Level</FieldLabel>
            <LogFilterCombobox
              options={logLevelOptions}
              value={selectedLogLevels}
              onChange={(values) => {
                setSelectedLogLevels(values as LogLevel[]);
              }}
              allLabel="All"
            />
          </Field>
          <Field size="xs" className="flex-1">
            <FieldLabel size="xs">Sandbox</FieldLabel>
            <LogFilterCombobox
              options={sandboxIdOptions}
              value={selectedSandboxIds}
              onChange={setSelectedSandboxIds}
              allLabel="All"
            />
          </Field>
        </div>
      </div>

      <StickToBottom className="relative min-h-0 flex-1 overflow-auto">
        <LogsContent filteredLogs={filteredLogs} />
        <CopyLogsButton logs={filteredLogs} />
        <ClearLogsButton
          logsCount={processLogs.length}
          onClearLogs={onClearLogs}
        />
        <ScrollToBottomButton />
      </StickToBottom>
    </div>
  );
};

export { LogsTab };
